import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { locationQueryKeys } from '@/lib/services/locations/locations.query'
import { reportQueryKeys } from '@/lib/services/reports/reports.query'
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query'
import {
  createAvailabilityChange,
  getAvailabilityChangeCalendar,
  getAvailabilityChangeCurrentSummary,
  getAvailabilityChanges,
  softDeleteAvailabilityChange,
  updateAvailabilityChange,
  type AvailabilityChangeCalendarQueryKeyEntries,
  type AvailabilityChangeIndexQueryKeyEntries,
  type AvailabilityChangeOrganizationScope,
} from './availability-changes.api'
import type {
  AvailabilityCalendarFilters,
  AvailabilityChangeIndexFilters,
  AvailabilityChangeInput,
} from './availability-changes.types'

/** The hooks fall back to the route's organization. */
interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface IndexFiltersScope {
  filters?: AvailabilityChangeIndexFilters | undefined
}

/** The calendar endpoint requires its date range. */
interface CalendarFiltersScope {
  filters: AvailabilityCalendarFilters
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type AvailabilityChangeIndexQueryKey = [AdminQueryRoot & AvailabilityChangeIndexQueryKeyEntries]
type AvailabilityChangeCalendarQueryKey = [
  AdminQueryRoot & AvailabilityChangeCalendarQueryKeyEntries,
]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const supportsAvailabilityChangesForOrganizationId = (
  organizationId: OrganizationIdInput
): boolean => Number(organizationId) !== Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)

export const availabilityChangeQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'availability-change' }, { organizationId }),
  ],
  index: ({
    organizationId,
    filters,
  }: AvailabilityChangeOrganizationScope & IndexFiltersScope): AvailabilityChangeIndexQueryKey =>
    buildScopedAdminQueryKey(availabilityChangeQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      filters,
    }),
  calendar: ({
    organizationId,
    filters,
  }: AvailabilityChangeOrganizationScope &
    CalendarFiltersScope): AvailabilityChangeCalendarQueryKey =>
    buildScopedAdminQueryKey(availabilityChangeQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'calendar',
      filters,
    }),
  currentSummary: ({ organizationId }: AvailabilityChangeOrganizationScope) =>
    buildScopedAdminQueryKey(availabilityChangeQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'currentSummary',
    }),
}

export const availabilityChangeQueries = {
  index: ({ organizationId, filters }: AvailabilityChangeOrganizationScope & IndexFiltersScope) =>
    indexTableQueryOptions({
      queryKey: availabilityChangeQueryKeys.index({ organizationId, filters }),
      queryFn: getAvailabilityChanges,
    }),

  calendar: ({
    organizationId,
    filters,
  }: AvailabilityChangeOrganizationScope & CalendarFiltersScope) =>
    queryOptions({
      queryKey: availabilityChangeQueryKeys.calendar({ organizationId, filters }),
      queryFn: getAvailabilityChangeCalendar,
    }),

  currentSummary: ({ organizationId }: AvailabilityChangeOrganizationScope) =>
    queryOptions({
      queryKey: availabilityChangeQueryKeys.currentSummary({ organizationId }),
      queryFn: getAvailabilityChangeCurrentSummary,
    }),
}

export function useAvailabilityChanges({
  organizationId,
  filters,
  ...params
}: OrganizationScope &
  IndexFiltersScope &
  QueryParams<ReturnType<typeof availabilityChangeQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...availabilityChangeQueries.index({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useAvailabilityChangeCalendar({
  organizationId,
  filters,
  ...params
}: OrganizationScope &
  CalendarFiltersScope &
  QueryParams<ReturnType<typeof availabilityChangeQueries.calendar>>) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...availabilityChangeQueries.calendar({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useAvailabilityChangeCurrentSummary({
  organizationId,
  ...params
}: OrganizationScope &
  QueryParams<ReturnType<typeof availabilityChangeQueries.currentSummary>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...availabilityChangeQueries.currentSummary({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

function invalidateAvailabilityChangeQueries(organizationId: number) {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: availabilityChangeQueryKeys.all({ organizationId }),
    }),
    queryClient.invalidateQueries({ queryKey: locationQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({
      queryKey: reportQueryKeys.closedDays({ organizationId }),
    }),
  ])
}

export function useCreateAvailabilityChangeMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: AvailabilityChangeInput) =>
      createAvailabilityChange({ organizationId, ...values }),
    onSuccess: () => invalidateAvailabilityChangeQueries(organizationId),
  })
}

export function useUpdateAvailabilityChangeMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: AvailabilityChangeInput & { id?: number; availabilityChangeId: number }) =>
      updateAvailabilityChange({ organizationId, ...values }),
    onSuccess: () => invalidateAvailabilityChangeQueries(organizationId),
  })
}

export function useSoftDeleteAvailabilityChangeMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (availabilityChangeId: number) =>
      softDeleteAvailabilityChange({ organizationId, availabilityChangeId }),
    onSuccess: () => invalidateAvailabilityChangeQueries(organizationId),
  })
}
